import React, { useState, useEffect, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import {
  Activity,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  RefreshCw,
  Link2,
  Info,
} from 'lucide-react';
import { usePatient } from '../../context/patientContext';
import { useHasAnyPermission } from '../../hooks/usePermission';
import { GrowthService } from '../../services/patientmr/growth.service';
import type { GrowthMeasurement } from '../../dtos/patientmr/growth.dto';

import {
  getBMICategory,
  getBMICDCData,
  getBMIPercentileLabel,
  getCDCData,
  getPercentileLabel,
  type GrowthMetric,
  type GrowthRange,
} from '../../utils/growthPercentiles';

ChartJS.register(LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

// ─── Types ───────────────────────────────────────────────────────────────────

type MetricType = GrowthMetric;
type RangeType = GrowthRange;

interface MetricConfig {
  label: string;
  unit: string;
  icon: string;
  colorHex: string;
  tabActiveText: string;
}

// ─── Metric configuration ────────────────────────────────────────────────────

const METRIC_CONFIG: Record<MetricType, MetricConfig> = {
  weight: {
    label: 'Weight',
    unit: 'kg',
    icon: '⚖️',
    colorHex: '#0F766E',
    tabActiveText: 'text-teal-600',
  },
  height: {
    label: 'Height',
    unit: 'cm',
    icon: '📏',
    colorHex: '#0D6861',
    tabActiveText: 'text-teal-700',
  },
  headCirc: {
    label: 'Head Circ.',
    unit: 'cm',
    icon: '🧠',
    colorHex: '#334155',
    tabActiveText: 'text-slate-700',
  },
  bmi: {
    label: 'BMI',
    unit: '',
    icon: '📊',
    colorHex: '#D97706',
    tabActiveText: 'text-amber-600',
  },
};

const METRICS: MetricType[] = ['weight', 'height', 'headCirc', 'bmi'];

// ─── CDC helpers (shared: utils/growthPercentiles) ───────────────────────────

function cdcToPoints(months: number[], values: number[]) {
  return months.map((m, i) => ({ x: m, y: values[i] }));
}

// ─── Utility helpers ──────────────────────────────────────────────────────────

function computeAgeMonths(dob: string, date: string): number {
  return Math.round(
    (new Date(date).getTime() - new Date(dob).getTime()) / (30.4375 * 24 * 60 * 60 * 1000)
  );
}

function formatAge(ageMonths: number): string {
  if (ageMonths < 12) return `${ageMonths}m`;
  const y = Math.floor(ageMonths / 12);
  const m = ageMonths % 12;
  return m === 0 ? `${y}y` : `${y}y ${m}m`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getMetricValue(m: GrowthMeasurement, metric: MetricType): number | null {
  if (metric === 'weight') return m.weight;
  if (metric === 'height') return m.height;
  if (metric === 'headCirc') return m.headCirc;
  return m.bmi;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface GrowthTrackingProps {
  expanded: boolean;
  toggleExpanded: () => void;
}

const GrowthTracking: React.FC<GrowthTrackingProps> = ({ expanded, toggleExpanded }) => {
  const { selectedPatient } = usePatient();
  const canReadGrowthChart = useHasAnyPermission([
    'patientmr:phi:growthchart:read' as any,
    'patientmr:phi:growthchart:readall' as any
  ]);
  const patientId = selectedPatient?.id as string | undefined;
  const dob = selectedPatient?.dob;
  const gender = selectedPatient?.gender ?? 'Male';

  // ── State ─────────────────────────────────────────────────────────────────
  const [activeMetric, setActiveMetric] = useState<MetricType>('weight');
  const [activeRange, setActiveRange] = useState<RangeType>('birth-36m');
  const [measurements, setMeasurements] = useState<GrowthMeasurement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  // ── Data ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!patientId || !expanded) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await GrowthService.getGrowthData(patientId);
      const responseDob = resp.patient?.dob;
      // If backend returned null ageMonths (e.g. dob missing at time of charting),
      // compute it client-side from the response patient.dob or context dob.
      const effectiveDob = (typeof responseDob === 'string' && responseDob) ? responseDob : dob;
      const normalized = (resp.measurements ?? []).map(m => ({
        ...m,
        ageMonths: m.ageMonths != null
          ? m.ageMonths
          : (effectiveDob && m.date ? computeAgeMonths(effectiveDob, m.date) : null),
      }));
      setMeasurements(normalized);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load growth data');
    } finally {
      setLoading(false);
    }
  }, [patientId, expanded, dob]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fixed ranges for head-circ and bmi
  useEffect(() => {
    if (activeMetric === 'headCirc') setActiveRange('birth-36m');
    if (activeMetric === 'bmi') setActiveRange('2-20y');
  }, [activeMetric]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const sorted = [...measurements].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  const mostRecent = sorted.length > 0 ? sorted[sorted.length - 1] : null;
  const prev = sorted.length > 1 ? sorted[sorted.length - 2] : null;

  // ── Chart ─────────────────────────────────────────────────────────────────
  const buildChartData = () => {
    const hex = METRIC_CONFIG[activeMetric].colorHex;
    const bandFill = 'rgba(240,253,250,0.45)';
    const refLine = 'rgba(203,213,225,0.85)';
    const medianLine = 'rgba(148,163,184,1)';

    let refs: any[] = [];

    if (activeMetric === 'bmi') {
      const d = getBMICDCData(gender);
      refs = [
        {
          label: '5th', data: cdcToPoints(d.months, d.p5),
          borderColor: refLine, borderWidth: 1, borderDash: [4, 2],
          pointRadius: 0, pointHoverRadius: 0, fill: '+1',
          backgroundColor: bandFill, tension: 0.4,
        },
        {
          label: '95th', data: cdcToPoints(d.months, d.p95),
          borderColor: refLine, borderWidth: 1, borderDash: [4, 2],
          pointRadius: 0, pointHoverRadius: 0, fill: false, tension: 0.4,
        },
        {
          label: '85th', data: cdcToPoints(d.months, d.p85),
          borderColor: 'rgba(203,213,225,0.55)', borderWidth: 1, borderDash: [3, 3],
          pointRadius: 0, pointHoverRadius: 0, fill: false, tension: 0.4,
        },
        {
          label: '50th', data: cdcToPoints(d.months, d.p50),
          borderColor: medianLine, borderWidth: 1.5, borderDash: [6, 3],
          pointRadius: 0, pointHoverRadius: 0, fill: false, tension: 0.4,
        },
      ];
    } else {
      const d = getCDCData(activeMetric, gender, activeRange);
      if (d) {
        refs = [
          {
            label: '3rd', data: cdcToPoints(d.months, d.p3),
            borderColor: refLine, borderWidth: 1, borderDash: [4, 2],
            pointRadius: 0, pointHoverRadius: 0, fill: '+1',
            backgroundColor: bandFill, tension: 0.4,
          },
          {
            label: '97th', data: cdcToPoints(d.months, d.p97),
            borderColor: refLine, borderWidth: 1, borderDash: [4, 2],
            pointRadius: 0, pointHoverRadius: 0, fill: false, tension: 0.4,
          },
          {
            label: '50th', data: cdcToPoints(d.months, d.p50),
            borderColor: medianLine, borderWidth: 1.5, borderDash: [6, 3],
            pointRadius: 0, pointHoverRadius: 0, fill: false, tension: 0.4,
          },
        ];
      }
    }

    const pts = measurements
      .filter(m => m.ageMonths != null && getMetricValue(m, activeMetric) != null)
      .map(m => ({ x: m.ageMonths as number, y: getMetricValue(m, activeMetric) as number }))
      .sort((a, b) => a.x - b.x);

    return {
      datasets: [
        ...refs,
        {
          label: 'Patient', data: pts,
          borderColor: hex, borderWidth: 2.5,
          pointRadius: 4, pointHoverRadius: 6,
          pointBackgroundColor: hex, pointBorderColor: '#FFFFFF', pointBorderWidth: 2,
          fill: false, tension: 0.3, spanGaps: true,
        },
      ],
    };
  };

  const getRange = () => {
    if (activeMetric === 'headCirc') return { min: 0, max: 36 };
    if (activeMetric === 'bmi') return { min: 24, max: 240 };
    return activeRange === 'birth-36m' ? { min: 0, max: 36 } : { min: 24, max: 240 };
  };

  const range = getRange();
  const unit = METRIC_CONFIG[activeMetric].unit;
  const is2_20 = activeRange === '2-20y' || activeMetric === 'bmi';

  const chartTitle =
    activeMetric === 'bmi'
      ? 'BMI-for-Age (2–20y)'
      : activeMetric === 'headCirc'
      ? 'Head Circumference (Birth–36m)'
      : `${METRIC_CONFIG[activeMetric].label} vs Age (${activeRange === 'birth-36m' ? 'Birth–36m' : '2–20y'})`;

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 250 },
    scales: {
      x: {
        type: 'linear' as const,
        min: range.min,
        max: range.max,
        title: {
          display: true, text: 'Age (months)',
          color: '#94A3B8', font: { size: 10 },
        },
        ticks: {
          color: '#94A3B8', font: { size: 10 }, maxTicksLimit: 12,
          callback: (v: any) => {
            const n = Number(v);
            if (is2_20) return n % 24 === 0 ? `${n / 12}y` : '';
            return n % 6 === 0 ? `${n}m` : '';
          },
        },
        grid: { color: '#F1F5F9', drawBorder: false },
      },
      y: {
        title: {
          display: true,
          text: unit ? `${METRIC_CONFIG[activeMetric].label} (${unit})` : 'BMI',
          color: '#94A3B8', font: { size: 10 },
        },
        ticks: { color: '#94A3B8', font: { size: 10 } },
        grid: { color: '#F1F5F9', drawBorder: false },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#FFFFFF',
        borderColor: '#E2E8F0',
        borderWidth: 1,
        titleColor: '#0F172A',
        bodyColor: '#334155',
        titleFont: { size: 11, weight: '600' },
        bodyFont: { size: 11 },
        padding: 8,
        callbacks: {
          title: (items: any[]) => {
            const age = items[0]?.parsed?.x;
            if (age == null) return '';
            if (age >= 12) {
              const y = Math.floor(age / 12);
              const mo = Math.round(age % 12);
              return `${Math.round(age)}m (${y}y${mo > 0 ? ` ${mo}m` : ''})`;
            }
            return `${Math.round(age)}m`;
          },
          label: (item: any) => {
            const val = item.parsed.y?.toFixed(1);
            const u = unit ? ` ${unit}` : '';
            if (item.dataset.label === 'Patient')
              return ` ${METRIC_CONFIG[activeMetric].label}: ${val}${u}`;
            return ` ${item.dataset.label}: ${val}${u}`;
          },
        },
      },
    },
  };

  // ── Summary helpers ───────────────────────────────────────────────────────
  const getTrend = (m: MetricType) => {
    const c = mostRecent ? getMetricValue(mostRecent, m) : null;
    const p = prev ? getMetricValue(prev, m) : null;
    if (c == null || p == null) return null;
    return c > p ? 'up' : c < p ? 'down' : 'flat';
  };

  const getCardPercentile = (m: MetricType): string | null => {
    if (!mostRecent || getMetricValue(mostRecent, m) == null || mostRecent.ageMonths == null)
      return null;
    const val = getMetricValue(mostRecent, m) as number;
    const age = mostRecent.ageMonths;
    if (m === 'bmi') return age >= 24 ? getBMIPercentileLabel(val, getBMICDCData(gender), age) : null;
    const r: RangeType = age <= 36 ? 'birth-36m' : '2-20y';
    const d = getCDCData(m, gender, r);
    return d ? getPercentileLabel(val, d, age) : null;
  };

  const isAlert = (pct: string | null) =>
    pct != null && ['<3rd', '>97th', '<5th', '≥95th'].includes(pct);

  const chartData = buildChartData();

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="mb-0 md:mb-2">
      <div className="bg-white md:rounded md:shadow overflow-visible md:border-b md:border-gray-200">

        {/* ── Collapse header ───────────────────────────────────────────── */}
        <div
          className="py-4 px-4 md:p-4 flex items-center justify-between cursor-pointer bg-slate-50 hover:bg-slate-100 active:bg-slate-200 md:bg-white md:hover:bg-slate-50 md:active:bg-slate-100 transition-colors border-b border-slate-200 md:border-gray-200"
          onClick={toggleExpanded}
          role="button"
          aria-expanded={expanded}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleExpanded();
            }
          }}
        >
          <h2 className="text-lg font-semibold flex items-center text-slate-800">
            <span className="mr-3 text-slate-600"><Activity size={20} /></span>
            Growth Tracking
            <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded font-medium leading-none">
              Pediatric
            </span>
          </h2>
          <ChevronDown
            size={20}
            className={`text-slate-600 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </div>

        {/* ── Expanded body ─────────────────────────────────────────────── */}
        {expanded && (
          <div className="p-3 md:p-4 lg:p-6 space-y-4">

            {/* Read-Only Notification */}
            {canReadGrowthChart && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-700">
                  Growth Tracking - View only
                </p>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="flex justify-center items-center gap-2 py-10 text-slate-400 text-sm">
                <RefreshCw size={18} className="animate-spin text-blue-400" />
                Loading growth data…
              </div>
            )}

            {/* Error */}
            {!loading && error && (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                <p className="text-xs text-red-700 flex-1">{error}</p>
                <button onClick={fetchData} className="text-xs text-red-600 underline">Retry</button>
              </div>
            )}

            {/* CHT-06: nothing charted yet */}
            {!loading && !error && measurements.length === 0 && (
              <div className="flex flex-col items-center text-center gap-2 py-10 px-4 border border-dashed border-slate-300 rounded-lg bg-slate-50" data-testid="growth-empty-state">
                <Info size={20} className="text-slate-400" />
                <p className="text-sm font-medium text-slate-700">No growth measurements yet</p>
                <p className="text-xs text-slate-500 max-w-sm">
                  Height, weight and head circumference appear here once a clinician charts a Measurements entry during a visit.
                </p>
              </div>
            )}

            {!loading && !error && measurements.length > 0 && (
              <>
                {/* ── Summary cards ──────────────────────────────────────── */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {METRICS.map(metric => {
                    const cfg = METRIC_CONFIG[metric];
                    const val = mostRecent ? getMetricValue(mostRecent, metric) : null;
                    const trend = getTrend(metric);
                    const pct = getCardPercentile(metric);
                    const alertPct = isAlert(pct);
                    const active = activeMetric === metric;

                    return (
                      <button
                        key={metric}
                        type="button"
                        onClick={() => setActiveMetric(metric)}
                        className={`text-left bg-white rounded-lg p-3 border transition-all hover:shadow-sm ${active ? '' : 'border-slate-200'}`}
                        style={active ? { borderColor: cfg.colorHex, borderLeftWidth: '3px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' } : {}}
                      >
                        {/* Label row */}
                        <div className="flex items-center gap-1 mb-1.5">
                          <span className="text-sm leading-none">{cfg.icon}</span>
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                            {cfg.label}
                          </span>
                        </div>

                        {/* Value + unit + trend */}
                        <div className="flex items-end gap-1 flex-wrap">
                          <span className="text-[22px] font-bold text-slate-900 leading-none">
                            {val != null
                              ? (metric === 'height' ? val.toFixed(0) : val.toFixed(1))
                              : <span className="text-slate-300">—</span>}
                          </span>
                          {cfg.unit && val != null && (
                            <span className="text-[11px] text-slate-400 mb-0.5">{cfg.unit}</span>
                          )}
                          {trend && val != null && (
                            <span className="mb-0.5 ml-0.5">
                              {trend === 'up' && <TrendingUp size={12} className="text-emerald-500" />}
                              {trend === 'down' && <TrendingDown size={12} className="text-red-400" />}
                              {trend === 'flat' && <Minus size={12} className="text-slate-400" />}
                            </span>
                          )}
                        </div>

                        {/* Percentile */}
                        {pct && (
                          <div className={`text-xs font-medium mt-1 ${alertPct ? 'text-red-500' : 'text-emerald-600'}`}>
                            {pct} %ile
                          </div>
                        )}

                        {/* BMI category badge */}
                        {metric === 'bmi' && val != null && mostRecent?.ageMonths != null && (mostRecent.ageMonths as number) >= 24 && (() => {
                          const cat = getBMICategory(val, getBMICDCData(gender), mostRecent.ageMonths as number);
                          return (
                            <span className={`inline-block text-xs px-1.5 py-0.5 rounded font-medium mt-1.5 ${cat.classes}`}>
                              {cat.label}
                            </span>
                          );
                        })()}

                        {/* No-data note for head circ */}
                        {metric === 'headCirc' && val == null && (
                          <div className="text-xs text-slate-300 mt-1">No data</div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* ── Chart section ──────────────────────────────────────── */}
                <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">

                  {/* Metric tabs */}
                  <div className="flex gap-0 border-b border-slate-200">
                    {METRICS.map(m => {
                      const cfg = METRIC_CONFIG[m];
                      const active = activeMetric === m;
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setActiveMetric(m)}
                          className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium transition-colors border-b-2 ${
                            active
                              ? 'bg-white border-current'
                              : 'bg-slate-50 border-transparent text-slate-500 hover:text-slate-700 hover:bg-white'
                          }`}
                          style={active ? { color: cfg.colorHex, borderColor: cfg.colorHex } : {}}
                        >
                          <span>{cfg.icon}</span>
                          <span>{cfg.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="p-3 sm:p-4">
                    {/* Chart header */}
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <span className="text-xs font-semibold text-slate-700">{chartTitle}</span>
                      <div className="flex items-center gap-3 flex-wrap">

                        {/* Range toggle — weight and height only */}
                        {(activeMetric === 'weight' || activeMetric === 'height') && (
                          <div className="flex gap-1">
                            {(['birth-36m', '2-20y'] as RangeType[]).map(r => {
                              const on = activeRange === r;
                              const hex = METRIC_CONFIG[activeMetric].colorHex;
                              return (
                                <button
                                  key={r}
                                  type="button"
                                  onClick={() => setActiveRange(r)}
                                  className={`px-2 py-0.5 text-xs rounded border font-medium transition-colors ${on ? 'bg-slate-50' : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'}`}
                                  style={on ? { color: hex, borderColor: hex, borderWidth: '1.5px' } : {}}
                                >
                                  {r === 'birth-36m' ? 'Birth–36m' : '2–20y'}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* Legend */}
                        <div className="flex items-center gap-3 text-xs text-slate-400">
                          <span className="flex items-center gap-1.5">
                            <svg width="20" height="8" aria-hidden="true">
                              <line x1="0" y1="4" x2="20" y2="4" stroke="rgba(148,163,184,0.85)" strokeWidth="1.5" strokeDasharray="5,3" />
                            </svg>
                            50th
                          </span>
                          <span className="flex items-center gap-1.5">
                            <span
                              className="inline-block w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: METRIC_CONFIG[activeMetric].colorHex }}
                            />
                            Patient
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Chart */}
                    <div className="h-56 sm:h-64 md:h-72">
                      <Line data={chartData} options={options} />
                    </div>
                  </div>
                </div>

                {/* ── History table ──────────────────────────────────────── */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setHistoryOpen(v => !v)}
                    className="w-full flex items-center gap-2 px-4 py-3 text-left bg-white hover:bg-slate-50 transition-colors"
                  >
                    <ChevronRight
                      size={13}
                      className={`text-slate-500 flex-shrink-0 transition-transform ${historyOpen ? 'rotate-90' : ''}`}
                    />
                    <span className="text-xs font-semibold text-slate-600">
                      Measurement History ({measurements.length})
                    </span>
                  </button>

                  {historyOpen && (
                    <div className="border-t border-slate-100">
                      {measurements.length === 0 ? (
                        <p className="px-4 py-4 text-xs text-slate-400 text-center italic">
                          No measurements recorded yet
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full">
                            <thead className="bg-slate-50">
                              <tr>
                                {['Date', 'Age', 'Wt (kg)', 'Ht (cm)', 'HC (cm)', 'BMI', 'Source', 'Provider'].map(col => (
                                  <th key={col} className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide whitespace-nowrap border-b border-slate-100">
                                    {col}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                              {[...measurements]
                                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                                .map(m => {
                                  const bmiWarn =
                                    m.bmi != null && m.ageMonths != null && (m.ageMonths as number) >= 24 &&
                                    getBMICategory(m.bmi, getBMICDCData(gender), m.ageMonths as number).label !== 'Healthy';
                                  return (
                                    <tr key={m.id} className="hover:bg-slate-50">
                                      <td className="px-3 py-2 text-xs text-slate-800 whitespace-nowrap">{formatDate(m.date)}</td>
                                      <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                                        {m.ageMonths != null ? formatAge(m.ageMonths) : '—'}
                                      </td>
                                      <td className="px-3 py-2 text-xs font-medium text-slate-900 whitespace-nowrap">
                                        {m.weight != null ? m.weight.toFixed(1) : '—'}
                                      </td>
                                      <td className="px-3 py-2 text-xs font-medium text-slate-900 whitespace-nowrap">
                                        {m.height != null ? m.height.toFixed(0) : '—'}
                                      </td>
                                      <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">
                                        {m.headCirc != null
                                          ? m.headCirc.toFixed(1)
                                          : <span className="text-slate-300">—</span>}
                                      </td>
                                      <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">
                                        {m.bmi != null ? (
                                          <span>
                                            {m.bmi.toFixed(1)}
                                            {bmiWarn && <span className="ml-1 text-amber-500 text-xs">⚠</span>}
                                          </span>
                                        ) : '—'}
                                      </td>
                                      <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                                        {m.source?.toLowerCase() === 'historical'
                                          ? <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">Historical</span>
                                          : m.source}
                                      </td>
                                      <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                                        {m.provider || '—'}
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Info banners ───────────────────────────────────────── */}
                <div className="space-y-2">
                  <div className="flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                    <Link2 size={13} className="text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-800 leading-relaxed">
                      <strong>Auto-synced from Visit Charting.</strong> Measurements recorded during visits flow here automatically.
                    </p>
                  </div>
                  <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                    <Info size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 leading-relaxed">
                      <strong>CDC 2000 reference:</strong> Weight &amp; Height use Birth–36m and 2–20y charts (toggle above).
                      Head circumference — Birth–36m only. BMI-for-age — 2–20y (5th/85th/95th for clinical categories).
                      Patient data always plotted even outside reference range.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GrowthTracking;
